<template>
  <div class="create-batch-modal">
    <md-button
      class="btn btn-primary create-batch-modal-main-button"
      @click="handleOpenModal"
    >
      <md-icon md-src="/static/images/icon-barcode.svg" />

      <span>Criar lote</span>
    </md-button>

    <modal
      ref="createBatchModalRef"
      template="neutral"
      ref-name="createBatchModalRef"
      :title="null"
      :icon-enabled="false"
      :close-button-enabled="false"
    >
      <spinner v-if="isLoading" />

      <aside v-else-if="step === CreateBatchStep.CreationStrategy">
        <header class="create-batch-modal-header">
          <h2 class="create-batch-modal-title">Como deseja continuar?</h2>
        </header>

        <div class="create-batch-modal-body">
          <pickable-buttons
            v-model="selectedOption"
            :options="options"
            track-options-by="id"
          />
        </div>

        <footer class="create-batch-modal-footer">
          <md-button
            class="btn btn-nonenary"
            @click="handleCancel"
          >
            Cancelar
          </md-button>

          <md-button
            class="btn btn-primary"
            :disabled="!hasSelectedOption"
            @click="handleContinue"
          >
            Continuar
          </md-button>
        </footer>
      </aside>

      <aside
        v-else-if="step === CreateBatchStep.ConfirmAutoBatch"
        class="create-batch-modal-confirm-auto-batch"
      >
        <template v-if="pendingSamplesCounter">
          <div class="create-batch-modal-body">
            <div class="create-batch-modal-body-subtitle">
              Deseja lotear {{ pendingSamplesCounter }} amostras automaticamente?
            </div>

            <div class="create-batch-modal-body-description">
              Ao continuar, {{ pendingSamplesCounter }} amostras serão loteadas conforme
              suas características de acondicionamento e destino.
            </div>
          </div>

          <footer class="create-batch-modal-footer">
            <md-button
              class="btn btn-nonenary"
              @click="handleCancel"
            >
              Cancelar
            </md-button>

            <md-button
              class="btn btn-primary"
              :disabled="!pendingSamplesCounter"
              @click="handleAutoBatchSamples"
            >
              Continuar
            </md-button>
          </footer>
        </template>

        <template v-else>
          <div class="create-batch-modal-body">
            <div class="create-batch-modal-body-subtitle">Não há amostras pendentes</div>

            <div class="create-batch-modal-body-description">
              Não encontramos amostras pendentes para lotear.
            </div>
          </div>

          <footer class="create-batch-modal-footer">
            <md-button
              class="btn btn-nonenary"
              @click="handleCancel"
            >
              Fechar
            </md-button>
          </footer>
        </template>
      </aside>
    </modal>

    <modal
      ref="loadingModal"
      ref-name="loadingModal"
      template="loading"
      title="Aguarde"
    >
      <p>Loteando amostras...</p>
    </modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

import PickableButtons from '@/components/common/PickableButtons'
import Spinner from '@/components/common/Spinner'

import sampleService from '@/api/sample'
import batchService from '@/api/batch'
import notify from '@/api/notification'

import { ONE_SECOND_IN_MILLISECONDS } from '@/constants'

const CreationBatch = {
  Auto: 'auto',
  Manual: 'manual',
}

const CreateBatchStep = {
  CreationStrategy: 'creationStrategy',
  ConfirmAutoBatch: 'confirmAutoBatch',
}

const AUTO_BATCH_SUCCESS_MESSAGE =
  'Loteamento automático de amostra(s) AOL realizado\ncom sucesso'

const options = [
  {
    id: CreationBatch.Auto,
    label: 'Loteamento automático',
    description: 'Lotear todas as amostras disponíveis.',
  },
  {
    id: CreationBatch.Manual,
    label: 'Loteamento manual',
    description: 'Criar um novo lote com características de uma amostra.',
  },
]

export default {
  name: 'CreateBatchModal',
  components: {
    PickableButtons,
    Spinner,
  },
  data() {
    return {
      isLoading: false,
      step: CreateBatchStep.CreationStrategy,
      selectedOption: undefined,
      pendingSamplesCounter: 0,
      options,
      lastPendingSamples: undefined,
      retryTimer: undefined,
      retryCount: 0,
      maxRetries: 10,
      CreateBatchStep,
    }
  },
  computed: {
    hasSelectedOption() {
      return !!this.selectedOption
    },
  },
  mounted() {
    clearTimeout(this.retryTimer)
  },
  methods: {
    ...mapActions(['clearAllSampleData', 'getServiceSample']),
    handleOpenModal() {
      this.$refs.createBatchModalRef.open()
    },
    async handleContinue() {
      switch (this.selectedOption.id) {
        case CreationBatch.Auto:
          this.isLoading = true

          try {
            const pendingSamplesCounterResponse =
              await sampleService.getPendingSamplesCounter()

            this.lastPendingSamples = pendingSamplesCounterResponse.data || []
            this.pendingSamplesCounter = this.lastPendingSamples.length
            this.step = CreateBatchStep.ConfirmAutoBatch
          } catch (error) {
            console.error('Error fetching pending samples counter', error)

            notify({
              title: 'Erro:',
              message:
                'Ocorreu um problema inesperado ao tentar verificar as amostras pendentes.',
            })
          } finally {
            this.isLoading = false
          }

          break

        case CreationBatch.Manual:
          this.$router.push('/create-batch')
      }
    },
    handleResetState() {
      this.isLoading = false
      this.step = CreateBatchStep.CreationStrategy
      this.selectedOption = undefined
      this.pendingSamplesCounter = 0
    },
    async handleCancel() {
      await this.$refs.createBatchModalRef.close()

      this.handleResetState()
    },
    async handleAutoBatchSamples() {
      this.isLoading = true

      try {
        await batchService.autoBatchSamples()
        this.startLoadingModal()
      } catch (error) {
        console.error('Error auto batching samples', error)

        notify({
          title: 'Erro:',
          message:
            error.response?.data?.message ||
            'Ocorreu um problema inesperado ao tentar lotear as amostras.',
        })
      }

      await this.$refs.createBatchModalRef.close()

      this.handleResetState()
    },
    startLoadingModal() {
      this.$refs.loadingModal.open()

      this.startRetryTimer()
    },
    startRetryTimer() {
      this.retryCount++

      this.retryTimer = setTimeout(async () => {
        const lastSample = this.lastPendingSamples[this.lastPendingSamples.length - 1]
        const lastSampleInfoResponse = await sampleService.getSampleByBarcode(lastSample)
        const lastSampleInfo = lastSampleInfoResponse.data
        const isSampleInABatch = !!(
          lastSampleInfo?.batch_identifier || lastSampleInfo?.batch_id
        )

        if (isSampleInABatch || this.retryCount >= this.maxRetries) {
          this.lastPendingSamples = undefined
          this.retryCount = 0

          clearTimeout(this.retryTimer)

          switch (this.$route.name) {
            case 'Impressão de Etiquetas - Lotes':
              this.$router.replace({
                query: {
                  ...this.$route.query,
                  reload: true,
                },
              })

              break

            case 'Impressão de Etiquetas - Amostras':
              await this.clearAllSampleData()
              await this.getServiceSample()

              break
          }

          this.$refs.loadingModal.close()

          if (isSampleInABatch) {
            notify({
              title: 'Sucesso:',
              message: 'Loteamento automático de amostra(s) AOL realizado com sucesso',
            })
          }
        } else {
          this.startRetryTimer()
        }
      }, ONE_SECOND_IN_MILLISECONDS * 3)
    },
  },
}
</script>

<style lang="scss" scoped>
@use '@/../static/stylesheets/variables' as *;

.create-batch-modal {
  font-family: 'Exo 2';

  .md-button {
    .md-icon {
      width: 1.375rem;
      height: 1.375rem;
      min-width: auto;
      min-height: auto;
    }
  }

  &-header {
    margin-bottom: 1rem;
  }

  &-title {
    font-size: 0.75rem;
    font-weight: 600;
    color: $theme-primary-color;
    text-transform: uppercase;
  }

  &-body {
    margin-bottom: 1rem;
    font-family: 'Exo 2';

    &-subtitle {
      font-weight: 700;
      font-style: bold;
      font-size: 1.125rem;
      color: #4a4a4a;
      margin-bottom: 0.5rem;
    }

    &-description {
      font-weight: 400;
      font-size: 1rem;
      color: #666666;
      line-height: 1.5;
    }
  }

  &-footer {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
  }

  &-main-button {
    display: flex;
    align-items: center;
    color: #ffffff;
    gap: 0.5rem;
    height: 40px;
  }

  &-confirm-auto-batch {
    max-width: 486px;
  }

  &-confirm-auto-batch &-footer {
    .md-button {
      flex: 1;
    }
  }
}
</style>
